import { Close, Download } from "@mui/icons-material";
import { DialogActions, Divider, Grid, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { AxiosError } from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useAnalysisProgress } from "../../hooks/useAnalysisProgress";
import { Analysis, AnalysisStatus, AnalysisStatusMapper } from "../../models/general/analysis";
import { URLS } from "../../services/app-urls";
import { BaseService } from "../../services/base-service";
import { translate } from "../../translate/translate";
import { ErrorComponent } from "../error/error.component";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { toast } from "react-toastify";
import { STORAGE_STATUS_TO_ENUM, AnalysisProgressStatus } from "../../websocket/domains/analysis/analysis.types";

interface AnalysisProcessingDialogProps {
  open: boolean;
  onClose: () => void;
  analysis: Analysis;
  setAnalysis: (analysis: Analysis) => void;
}

const AnalysisProcessingDialog: React.FC<AnalysisProcessingDialogProps> = ({
                                                                             open,
                                                                             onClose,
                                                                             analysis,
                                                                           setAnalysis,
                                                                           }) => {
  const {
    user_name: userName,
    processing_started_at: startTime,
    processing_ended_at,
    status: initialStatus,
    period: period,
    boms: initialBoms,
  } = analysis;

  const analysisInfo = useAnalysisProgress(analysis.id);

  const service = new BaseService<Analysis>(URLS.ANALYSIS);
  const serviceAnalysisLog = new BaseService<Blob>(URLS.ANALYSIS);

  const [status, setStatus] = useState<AnalysisStatus>(
    initialStatus || AnalysisStatus.PROCESSING,
  );
  const [duration, setDuration] = useState<number>();
  const [percentage, setPercentage] = useState<number>();
  const [endTime, setEndTime] = useState<string | undefined>(
    processing_ended_at,
  );
  const [showMessage, setShowMessage] = useState(false);

  const [bomsString, setBomsString] = useState("");

  useEffect(() => {
    if (initialStatus == AnalysisStatus.COMPLETED && processing_ended_at) {
      const diff =
        new Date(processing_ended_at).getTime() -
        new Date(startTime || "").getTime();
      setDuration(diff / 1000);
    }
  }, []);

  useEffect(() => {
    if (!analysisInfo) return;

    if (analysisInfo.progress !== undefined) setPercentage(analysisInfo.progress);

    const enumStatus = STORAGE_STATUS_TO_ENUM[analysisInfo.status as AnalysisProgressStatus];
    if (enumStatus !== undefined) setStatus(enumStatus);

    if (analysisInfo.elapsed !== undefined) setDuration(analysisInfo.elapsed);
    if (analysisInfo.ended_at !== undefined) setEndTime(analysisInfo.ended_at);
  }, [analysisInfo]);

  const formatDurationTime: string = useMemo(() => {
    if (!duration) return "...";

    const totalSeconds = Math.round(duration);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return hours > 0
      ? minutes > 0
        ? `${hours}h ${minutes}min`
        : `${hours}h`
      : minutes > 0
        ? seconds > 0
          ? `${minutes}min ${seconds}s`
          : `${minutes}min`
        : seconds > 0
          ? `${seconds}s`
          : "1s";
  }, [duration]);

  useEffect(() => {
    if (initialBoms && initialBoms.length > 0) {
      const newBomsString = initialBoms.join(", ");
      setBomsString(newBomsString);
    } else {
      setBomsString("");
    }
  }, [initialBoms]);

  const formatPercentage: string = useMemo(() => {
    if (status === AnalysisStatus.PROCESSING && percentage) {
      return ` (${percentage}%)`;
    }
    return "";
  }, [status, percentage]);

  const formatDateTime = (dateTimeString?: string): string => {
    if (!dateTimeString) return "";

    const date = new Date(dateTimeString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}/${month}/${year} - ${hours}h${minutes}min`;
  };

  const downloadErrorLog = async () => {
    try {
      serviceAnalysisLog.clearParameters();
      const response = await serviceAnalysisLog.getAnalysisErrorLog(
        analysis.id,
      );

      const blob = new Blob([response.data], {
        type: response.headers["content-type"] as string,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "Analysis_Error_Log.txt");
      document.body.appendChild(link);
      link.click();

      link.parentNode?.removeChild(link);
    } catch (error) {
      const axiosError = error as AxiosError;
      ErrorComponent(axiosError, false);
    }
  };

  const onCancelAnalysis = async () => {
    service.clearParameters();
    service
      .update(
        {
          status: AnalysisStatus.CANCELED,
        },
        analysis?.id,
      )
      .then(({ data }) => {
        setAnalysis(data);
        onClose();
      })
      .catch(error => ErrorComponent(error));
  };

  const handleCopy = async () => {
    try {
      if (bomsString) {
        await navigator.clipboard.writeText(bomsString);
      }
      setShowMessage(true);
    } catch (err) {
      toast.error(translate.t("copy_failed"));
    }
    setTimeout(() => {
      setShowMessage(false);
    }, 2000);
  };

  return (
    <Dialog open={open} onClose={() => onClose()}>
      <Grid
        padding={2}
        container
        justifyContent="space-between"
        sx={{ backgroundColor: "primary.main" }}
      >
        <Grid item>
          <Typography
            sx={{ color: "#ffffff", fontWeight: "bold", fontSize: "16px" }}
          >
            {translate.t("analysis_processing")}
          </Typography>
        </Grid>
        <Grid item sx={{ cursor: "pointer", color: "#ffffff" }}>
          <Close onClick={() => onClose()} />
        </Grid>
      </Grid>
      <DialogContent sx={{ minWidth: "500px" }}>
        <Grid container flexDirection="column" gap={1}>
          <Grid item>
            <Typography>
              <b>{translate.t("user")}:</b> {userName}
            </Typography>
          </Grid>
          <Grid item>
            <Typography>
              <b>{translate.t("processing_start_time")}:</b>{" "}
              {formatDateTime(startTime)}
            </Typography>
          </Grid>
          {status !== AnalysisStatus.CANCELED &&
            status !== AnalysisStatus.PROCESSING && (
              <>
                <Grid item>
                  <Typography>
                    <b>{translate.t("execution_time")}:</b> {formatDurationTime}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography>
                    <b>{translate.t("processing_end_time")}:</b>{" "}
                    {formatDateTime(endTime)}
                  </Typography>
                </Grid>
              </>
            )}
          <Grid item>
            <Typography>
              <b>{translate.t("status")}:</b>{" "}
              {status ? AnalysisStatusMapper[status] : ""}
              {formatPercentage}
            </Typography>
          </Grid>

          <Divider></Divider>

          <Grid item>
            <Typography>
              <b>{translate.t("period")}:</b> {period || "-"} {translate.t("weeks")}
            </Typography>
          </Grid>

          <Grid item>
            <Typography>
              <b>{translate.t("bom_quantity")}:</b> {initialBoms?.length}
            </Typography>
          </Grid>

          <Grid item>
            <Grid sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography>
                <b>{translate.t("BOMs")}:</b>
              </Typography>

              <Grid
                sx={{
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                  gap: "2px",
                  cursor: "pointer",
                  transition: "background-color 0.3s ease, transform 0.2s ease",
                  "&:hover": {
                    backgroundColor: (theme) => theme.palette.action.hover,
                  },
                  "&:active": {
                    backgroundColor: (theme) => theme.palette.action.selected,
                  },
                }}
                onClick={handleCopy}
              >
                <ContentCopyOutlinedIcon fontSize={"small"} color={"primary"} />
                <Typography color={"primary"}>
                  <b>{translate.t(showMessage ? "copied" : "copy")}</b>
                </Typography>
              </Grid>

            </Grid>

            <Grid sx={{
              "& .MuiInputBase-root": {
                padding: "5px 0px 5px 5px",
              },
              marginTop: "10px",
            }}>
              <TextField
                multiline
                rows={7}
                defaultValue={bomsString}
                fullWidth
                disabled={true}
                sx={{
                  "& .MuiInputBase-input": {
                    "&[disabled]": {
                      color: "#607883",
                      WebkitTextFillColor: "#607883",
                    },
                  },
                  backgroundColor: "#FAFAFA",
                }}
              />
            </Grid>

          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent="flex-end" gap={1} padding={2}>
          <Grid item>
            <Button
              color="primary"
              variant="outlined"
              onClick={() => onClose()}
            >
              {translate.t("close")}
            </Button>
          </Grid>
          {status && status === AnalysisStatus.PROCESSING && (
            <Grid item>
              <Button
                variant="contained"
                color="error"
                onClick={() => onCancelAnalysis()}
                startIcon={<Close />}
              >
                {translate.t("cancel_processing")}
              </Button>
            </Grid>
          )}
          {status && status === AnalysisStatus.ERROR && (
            <Grid item>
              <Button
                variant="contained"
                color="error"
                onClick={() => downloadErrorLog()}
                startIcon={<Download />}
              >
                {translate.t("download_log")}
              </Button>
            </Grid>
          )}
        </Grid>
      </DialogActions>
    </Dialog>
  );
};

export default AnalysisProcessingDialog;
